import { useEffect, useMemo, useState } from 'react';
import { useMyClubsQuery } from '../api/queries';
import type { MyClub } from './types';

/** Klucz localStorage z ostatnio wybranym klubem. */
const CLUB_STORAGE_KEY = 'tenify.dashboard.clubId';

export interface SelectedClub {
  /** Kluby, ktorych uzytkownik jest czlonkiem. */
  clubs: readonly MyClub[];
  /** Identyfikator aktywnego klubu; `undefined` dopoki lista sie nie wczyta. */
  clubId: string | undefined;
  /** Pelny rekord aktywnego klubu, razem z rola uzytkownika w nim. */
  club: MyClub | undefined;
  selectClub: (id: string) => void;
  isLoading: boolean;
  /** Lista wczytana i pusta — uzytkownik nie nalezy do zadnego klubu. */
  isEmpty: boolean;
}

/**
 * Wybor klubu, w ktorego kontekscie pracuja widoki zarzadcze.
 *
 * Stan siedzi w `localStorage`, a nie w adresie, bo to preferencja uzytkownika,
 * a nie czesc widoku. Wspolny hook zamiast kopii w kazdym ekranie ma znaczenie
 * praktyczne: inaczej strona startowa i centrum turniejow potrafilyby pokazywac
 * dane dwoch roznych klubow naraz.
 */
export function useSelectedClub(): SelectedClub {
  const clubsQuery = useMyClubsQuery();
  const [storedClubId, setStoredClubId] = useState<string | undefined>(
    () => localStorage.getItem(CLUB_STORAGE_KEY) ?? undefined,
  );

  const clubs = useMemo(() => clubsQuery.data ?? [], [clubsQuery.data]);

  // Zapamietany wybor obowiazuje, dopoki klub istnieje; inaczej pierwszy z listy.
  const clubId = useMemo(() => {
    if (storedClubId && clubs.some((club) => club.id === storedClubId)) {
      return storedClubId;
    }
    return clubs[0]?.id;
  }, [storedClubId, clubs]);

  useEffect(() => {
    if (clubId) localStorage.setItem(CLUB_STORAGE_KEY, clubId);
  }, [clubId]);

  const club = useMemo(() => clubs.find((item) => item.id === clubId), [clubs, clubId]);

  return {
    clubs,
    clubId,
    club,
    selectClub: setStoredClubId,
    isLoading: clubsQuery.isPending,
    isEmpty: clubsQuery.isSuccess && clubs.length === 0,
  };
}
